import Image from 'next/image'
import { useEffect, useState, useRef } from 'react'
import { sanitizeHTML } from '@/lib/utils'

interface SecTypeProps {
	heading?: {
		title?: string
	}
	text?: {
		text?: string
	}
	content_collection?: {
		id: string
		site_content_collections_id: {
			collection: {
				item: {
					title: string | null
					text: string | null
				}
			}[]
		}
	}[]
}

export const fields = [
	// headings
	'heading.title',

	// text
	'text.text',

	// content collection
	'content_collection.id',
	'content_collection.site_content_collections_id.collection.item:site_content.title',
	'content_collection.site_content_collections_id.collection.item:site_content.text',
]

export default function SecType({ data }: { data: SecTypeProps }) {
	const [isInView, setIsInView] = useState(true);
	const sectionRef = useRef<HTMLElement>(null);

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				setIsInView(entry.isIntersecting);
			},
			{
				threshold: 0.1,
			}
		);

		if (sectionRef.current) {
			observer.observe(sectionRef.current);
		}

		return () => {
			observer.disconnect();
		};
	}, []);

	return (
		<section id='types' ref={sectionRef} className='SecTypes py-16 md:py-28 bg-white'>
			<div className='container'>
				<div className='flex flex-col gap-8'>
					{/* Header Section */}
					<div className='flex flex-wrap md:flex-row-reverse justify-between items-start'>
						<div className='md:w-1/2 flex justify-center max-md:mb-12 max-md:mx-auto'>
							<div className='circle-graphic relative w-[189px] h-48'>
								<Image
									src='/images/circle.svg'
									alt='Circle graphic'
									fill
									className='object-contain will-change-transform [transform:translateZ(0)] [backface-visibility:hidden]'
									style={{
										animation: 'secTypesSpin 20s linear infinite',
										WebkitAnimation: 'secTypesSpin 20s linear infinite',
										animationPlayState: isInView ? 'running' : 'paused',
										WebkitAnimationPlayState: isInView ? 'running' : 'paused',
										transformOrigin: '50% 50%',
									}}
									sizes='189px'
								/>
							</div>
						</div>
						<div className='md:w-1/2 flex flex-col gap-8 md:gap-12'>
							<h2>{data?.heading?.title || ''}</h2>
							<div
								className='grid gap-8'
								dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || '')}
							/>
						</div>
					</div>

					{/* Procedures Section */}
					<div className='grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16'>
						{data?.content_collection?.map((item) => {
							const titleItem = item.site_content_collections_id.collection.find(c => c.item.title);
							const textItem = item.site_content_collections_id.collection.find(c => c.item.text);
							
							return (
								<div key={item.id} className='flex flex-col gap-8'>
									{titleItem?.item.title ? <h3>{titleItem.item.title}</h3> : null}
									<div
										className='grid gap-8'
										dangerouslySetInnerHTML={sanitizeHTML(textItem?.item.text || '')}
									/>
								</div>
							)
						})}
					</div>
				</div>
			</div>
			<style jsx>{`
				@-webkit-keyframes secTypesSpin {
					from {
						-webkit-transform: rotate(0deg);
						transform: rotate(0deg);
					}
					to {
						-webkit-transform: rotate(360deg);
						transform: rotate(360deg);
					}
				}

				@keyframes secTypesSpin {
					from {
						-webkit-transform: rotate(0deg);
						transform: rotate(0deg);
					}
					to {
						-webkit-transform: rotate(360deg);
						transform: rotate(360deg);
					}
				}
			`}</style>
		</section>
	)
}